// The snippet's More/Less toggle (`extension-alert-candidate-snippet.component.ng1.ts:300-345`, and
// the client-contact twin) — which state it is in, and the URL each direction lands on.
//
// Measured on build 354, standalone candidate profile 17360, 29 Oct:
//   …/profile?candidateId=17360                       → "More"
//   click More → …/profile/more?originSnippet=1&candidateId=17360&algoScore=NaN   → "Less"
//   click Less → …/profile?originSnippet=1&candidateId=17360&algoScore=NaN         → "More"
//   direct …/more?candidateId=17360&algoScore=NaN     → "Less"
//   direct …/more?candidateId=17360                   → "More"
//   direct …/more?candidateId=17360&algoScore=160     → "Less"

/**
 * `isInViewMoreState()` — `$state.includes(viewMoreState, {alertId, candidateId, algoScore})`,
 * where the snippet's `algoScore` is `parseFloat($stateParams.algoScore)`. The comparison is of
 * the ENCODED values, so `NaN` matches the text "NaN" (a toggled URL reads "Less"), "160" matches
 * 160, and an absent `algoScore` matches nothing.
 */
export function isInViewMore(onMoreRoute: boolean, rawAlgoScore: string | null): boolean {
  return onMoreRoute && rawAlgoScore !== null && String(Number.parseFloat(rawAlgoScore)) === rawAlgoScore
}

/**
 * `$state.href(state, params)` order for these states: `originSnippet`, the entity id, `algoScore`,
 * `alertId`, then anything else already on the URL — `$state.go` inherits the current params.
 */
function ordered(idKey: string, values: Record<string, string | undefined>, rest: URLSearchParams): string {
  const query = new URLSearchParams()
  for (const key of ['originSnippet', idKey, 'algoScore', 'alertId']) {
    const value = values[key]
    if (value !== undefined) query.set(key, value)
  }
  for (const [key, value] of rest) if (!query.has(key) && !(key in values)) query.append(key, value)
  return query.toString()
}

/**
 * `goToViewMoreState()`: `originSnippet: isInSnippetView()` (true from the snippet), the id, and
 * `algoScore` as the snippet holds it — `parseFloat` of the current one, so an absent score goes
 * out as the text "NaN".
 */
export function viewMoreQuery(idKey: string, current: URLSearchParams): string {
  const raw = current.get('algoScore')
  return ordered(
    idKey,
    {
      originSnippet: '1',
      [idKey]: current.get(idKey) ?? undefined,
      algoScore: String(Number.parseFloat(raw ?? '')),
      alertId: current.get('alertId') ?? undefined,
    },
    current,
  )
}

/**
 * `goToViewLessState()`: the same id/algoScore/alertId, and `originSnippet` INHERITED from the
 * current URL (`$state.go` keeps it) — the port deleted it, upstream keeps `originSnippet=1`.
 */
export function viewLessQuery(idKey: string, current: URLSearchParams): string {
  const raw = current.get('algoScore')
  return ordered(
    idKey,
    {
      originSnippet: current.get('originSnippet') ?? undefined,
      [idKey]: current.get(idKey) ?? undefined,
      algoScore: raw === null ? undefined : String(Number.parseFloat(raw)),
      alertId: current.get('alertId') ?? undefined,
    },
    current,
  )
}
